import moment from 'moment';
import normalizeSearchWidgetDateFormat from 'utils/normalizeSearchWidgetDateFormat';
/**
 * Resolution of the after-midnight divider variant, kept out of the component on purpose — the same
 * split `utils/specialDiscount.js` uses for `getSpecialDiscountVariant`. The divider never learns
 * where its variant came from, so the selection mechanism can change without touching the markup.
 */

const FLAG = 'AFTER_MIDNIGHT_DIVIDER_VARIANT';

/**
 * The three divider presentations, ordered by how much visual weight they carry.
 * Named for that axis rather than after the mockup files: mockup numbers read as successive
 * versions in code when these are live alternatives, and a name tied to a colour would become a lie
 * the first time the palette is tweaked.
 */
export const AFTER_MIDNIGHT_VARIANTS = ['prominent', 'soft', 'minimal'];

export const DEFAULT_AFTER_MIDNIGHT_VARIANT = 'prominent';

/**
 * Get the after-midnight divider variant for the current brand.
 *
 * This chooses a presentation only. It does NOT decide whether the section renders — that is the
 * presence of trips in the payload, gated server-side per brand, exactly as closed trips are.
 *
 * @param {object} features - Whitelabel feature flags.
 * @returns {'prominent'|'soft'|'minimal'} The variant, falling back when the flag is absent or holds
 *   a value outside the three.
 */
export default function getAfterMidnightVariant(features) {
  const value = features?.[FLAG];

  return AFTER_MIDNIGHT_VARIANTS.includes(value) ? value : DEFAULT_AFTER_MIDNIGHT_VARIANT;
}

/**
 * Whether the after-midnight section should be mounted.
 *
 * Extracted from the listing's render so the rule can be exercised on its own: inline in a
 * 1300-line class component the only way to reach it would be to mount the whole page.
 *
 * Two contexts are excluded, and neither is a scope preference — the listing's own list component
 * carries effects that a second instance trips over there:
 *
 * - **Exchange.** It auto-selects and navigates when it holds exactly one trip, and a one-trip
 *   section is this feature's most likely size.
 * - **The open-ticket route.** Its unfiltered branch returns `null` into a component that slices
 *   the value, which throws.
 *
 * @param {object} params - Params.
 * @param {Array} params.trips - The section trips, already scoped.
 * @param {boolean} params.isOpenTicketList - Whether the open-ticket listing is showing.
 * @param {boolean} params.isExchange - Whether the page is in the exchange flow.
 * @returns {boolean} Whether to mount the section.
 */
export function shouldShowAfterMidnightSection({ trips, isOpenTicketList, isExchange }) {
  return Boolean(trips?.length) && !isOpenTicketList && !isExchange;
}

/** Same shape the section's divider prints, so the heading and the cards agree on one screen. */
const SECTION_DATE_FORMAT = 'DD-MMM-YY';

/** Not a digit and not a separator: the month name, wherever the brand's order puts it. */
const MONTH_NAME = /[^\d\-/\s.]+/;

/**
 * The date of a small-hours trip, for the capsule on its card.
 *
 * Matched by shape and not by position because the brand's format decides where the month lands:
 * capitalizing "the second token" would miss a month-first brand entirely.
 *
 * @param {string} departure - The trip's departure timestamp.
 * @param {string} brandDateFormat - `searchWidget.dateFormat`, when the brand configures one.
 * @returns {string} The date, in the brand's own order and with the month capitalized.
 */
export function formatSectionDate(departure, brandDateFormat) {
  return moment(departure)
    .format(normalizeSearchWidgetDateFormat(brandDateFormat, SECTION_DATE_FORMAT))
    .replace(MONTH_NAME, (month) => `${month.charAt(0).toUpperCase()}${month.slice(1)}`);
}
